import { Helmet } from "react-helmet-async";

const SITE_URL = "https://accessbharat.com";
const SITE_NAME = "AccessBharat";
const DEFAULT_TITLE =
  "WCAG Compliance India | Web Accessibility Audit & Testing Services | AccessBharat";
const DEFAULT_DESCRIPTION =
  "AccessBharat is India's accessibility testing company — WCAG 2.2, IS 17802 and GIGW audits, VPATs, and remediation for websites, apps and documents.";
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.png`;
const TWITTER_HANDLE = "@accessbharat";
const CONTACT_EMAIL = "priya@accessbharat.com";
/** Search engines truncate titles past ~60 chars and descriptions past ~160. */
const MAX_TITLE_LENGTH = 60;
const MAX_DESCRIPTION_LENGTH = 160;

/**
 * og:image URL for a page image. Facebook, LinkedIn, X and WhatsApp reject SVG
 * share images, so an `.svg` maps to its 1200×630 `.png` sibling.
 */
function shareImage(image: string): string {
  return image.replace(/\.svg$/i, ".png");
}

export interface SeoProps {
  /** Page-specific title. The site name is appended automatically unless the title already contains it. */
  title?: string;
  description?: string;
  /** Path (e.g. "/blog/my-post") or absolute URL used for the canonical + og:url. Defaults to "/". */
  path?: string;
  /** Absolute or site-relative image URL for og:image / twitter:image. */
  image?: string;
  /** og:type — "website" for pages, "article" for blog posts. */
  type?: "website" | "article";
  /** Discourage indexing (e.g. 404). */
  noindex?: boolean;
  /** One or more JSON-LD objects to embed as <script type="application/ld+json">. */
  jsonLd?: object | object[];
}

function absoluteUrl(pathOrUrl: string): string {
  if (/^https?:\/\//.test(pathOrUrl)) return pathOrUrl;
  return `${SITE_URL}${pathOrUrl.startsWith("/") ? "" : "/"}${pathOrUrl}`;
}

/**
 * Per-route document head: title, description, canonical, Open Graph, Twitter
 * card, robots, and optional JSON-LD structured data. Requires <HelmetProvider>
 * at the app root. (NCS-194)
 */
export function Seo({
  title,
  description = DEFAULT_DESCRIPTION,
  path = "/",
  image = DEFAULT_OG_IMAGE,
  type = "website",
  noindex = false,
  jsonLd,
}: SeoProps) {
  // Only append the site name when the result still fits a search-result title
  // (~60 chars); otherwise Google truncates the page's own words instead.
  const suffixed = title ? `${title} | ${SITE_NAME}` : "";
  const fullTitle = !title
    ? DEFAULT_TITLE
    : title.includes(SITE_NAME) || suffixed.length > MAX_TITLE_LENGTH
      ? title
      : suffixed;
  const canonical = absoluteUrl(path);
  const ogImage = absoluteUrl(shareImage(image));
  const blocks = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={TWITTER_HANDLE} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {blocks.map((block, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(block)}
        </script>
      ))}
    </Helmet>
  );
}

/**
 * Sitewide Organization JSON-LD — the core entity signal that lets search and
 * AI answer engines resolve "AccessBharat" to a real company with a location,
 * contact, service area, and areas of expertise. Typed as `ProfessionalService`
 * (a subtype of Organization + LocalBusiness) since the site sells a service.
 */
export const ORGANIZATION_JSONLD = {
  "@context": "https://schema.org",
  "@type": ["Organization", "ProfessionalService"],
  "@id": `${SITE_URL}/#organization`,
  name: SITE_NAME,
  alternateName: "AccessBharat Accessibility",
  url: SITE_URL,
  logo: `${SITE_URL}/favicon.png`,
  image: DEFAULT_OG_IMAGE,
  description: DEFAULT_DESCRIPTION,
  email: CONTACT_EMAIL,
  areaServed: { "@type": "Country", name: "India" },
  address: {
    "@type": "PostalAddress",
    addressLocality: "Mumbai",
    addressRegion: "Maharashtra",
    addressCountry: "IN",
  },
  contactPoint: {
    "@type": "ContactPoint",
    contactType: "sales",
    email: CONTACT_EMAIL,
    areaServed: "IN",
    availableLanguage: ["en", "hi"],
  },
  knowsAbout: [
    "Web Content Accessibility Guidelines (WCAG) 2.2",
    "IS 17802 Indian accessibility standard",
    "Guidelines for Indian Government Websites (GIGW 3.0)",
    "Rights of Persons with Disabilities (RPwD) Act 2016",
    "European Accessibility Act",
    "VPAT / Accessibility Conformance Report",
    "PDF and document accessibility",
    "Mobile app accessibility",
    "Accessibility remediation",
  ],
  sameAs: [] as string[],
};

export const WEBSITE_JSONLD = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${SITE_URL}/#website`,
  name: SITE_NAME,
  url: SITE_URL,
  publisher: { "@id": `${SITE_URL}/#organization` },
  inLanguage: "en-IN",
};

/**
 * The services AccessBharat offers, as a `Service` with an `OfferCatalog`.
 * Gives answer engines a structured, citable list of what the company does —
 * mirror of the human-readable Services section.
 */
export const SERVICE_JSONLD = {
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: "Web accessibility audit and WCAG compliance",
  provider: { "@id": `${SITE_URL}/#organization` },
  areaServed: { "@type": "Country", name: "India" },
  description:
    "WCAG 2.2, IS 17802 and GIGW audits, VPATs, and accessibility remediation for websites, mobile apps and documents.",
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Accessibility services",
    itemListElement: [
      "WCAG 2.2 AA website accessibility audit",
      "Mobile app accessibility testing",
      "PDF and document accessibility remediation",
      "VPAT / Accessibility Conformance Report",
      "GIGW 3.0 government website compliance",
      "Accessibility remediation and developer support",
    ].map((name) => ({
      "@type": "Offer",
      itemOffered: { "@type": "Service", name },
    })),
  },
};

/**
 * Frequently-asked questions shown on the homepage AND emitted as `FAQPage`
 * JSON-LD. Plain-language Q&A is the single richest AEO signal — answer engines
 * lift these directly. Keep questions phrased the way a prospect would ask.
 */
export const FAQ_ITEMS: { question: string; answer: string }[] = [
  {
    question: "What is WCAG compliance?",
    answer:
      "WCAG (Web Content Accessibility Guidelines) compliance means a website or app meets the W3C's technical standard for accessibility so people with disabilities can perceive, operate and understand it. The current version is WCAG 2.2, and most audits target Level AA. AccessBharat audits websites, mobile apps and PDFs against WCAG 2.2 AA.",
  },
  {
    question: "Is digital accessibility legally required in India?",
    answer:
      "Yes. The Rights of Persons with Disabilities (RPwD) Act 2016 requires accessible information and services, government websites must follow GIGW 3.0 (which adopts IS 17802 / WCAG), and sector regulators such as SEBI have issued accessibility circulars. Private businesses serving the public are increasingly expected to meet WCAG 2.2 AA.",
  },
  {
    question: "What is IS 17802 and how does it relate to WCAG?",
    answer:
      "IS 17802 is India's official accessibility standard for ICT products and services, published by the Bureau of Indian Standards. It is harmonised with WCAG 2.x and EN 301 549, so meeting WCAG 2.2 AA satisfies the core of IS 17802.",
  },
  {
    question: "How long does a website accessibility audit take?",
    answer:
      "A typical audit runs two to four weeks depending on the number of templates and user journeys. AccessBharat combines automated scanning with expert manual and assistive-technology testing, then delivers a prioritised report, a VPAT where needed, and remediation guidance.",
  },
  {
    question: "What do I get at the end of an accessibility audit?",
    answer:
      "You receive a detailed conformance report mapped to WCAG 2.2 success criteria, severity-ranked issues with reproduction steps and fix guidance, screenshots, and — on request — a VPAT / Accessibility Conformance Report and an engagement letter. AccessBharat can also support your team through remediation.",
  },
  {
    question: "Do accessibility overlays or widgets make my site compliant?",
    answer:
      "No. Automated overlay widgets cannot fix most accessibility barriers and can introduce new ones. Genuine compliance requires fixing the underlying code and content, verified by manual and assistive-technology testing — which is what AccessBharat provides.",
  },
];

/** `FAQPage` JSON-LD built from an ordered list of `{ question, answer }`. */
export function faqPageJsonLd(items: { question: string; answer: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((it) => ({
      "@type": "Question",
      name: it.question,
      acceptedAnswer: { "@type": "Answer", text: it.answer },
    })),
  };
}

/**
 * `BlogPosting` JSON-LD for a single post — the core AEO signal that lets search
 * and AI answer engines cite the article with the right headline, image, dates,
 * author, and publisher.
 */
export function blogPostingJsonLd(post: {
  title: string;
  excerpt: string;
  path: string;
  image?: string;
  datePublished: string;
  dateModified?: string;
  author: string;
  /** Topical tag(s) — becomes `keywords` + `articleSection`. */
  keywords?: string;
  /** Approximate body word count. */
  wordCount?: number;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.excerpt,
    url: absoluteUrl(post.path),
    mainEntityOfPage: absoluteUrl(post.path),
    ...(post.image ? { image: absoluteUrl(shareImage(post.image)) } : {}),
    datePublished: post.datePublished,
    dateModified: post.dateModified ?? post.datePublished,
    inLanguage: "en-IN",
    isAccessibleForFree: true,
    ...(post.keywords ? { keywords: post.keywords, articleSection: post.keywords } : {}),
    ...(post.wordCount ? { wordCount: post.wordCount } : {}),
    author: { "@type": "Organization", name: post.author, url: SITE_URL },
    publisher: {
      "@type": "Organization",
      name: SITE_NAME,
      logo: { "@type": "ImageObject", url: `${SITE_URL}/favicon.png` },
    },
  };
}

/** `BreadcrumbList` JSON-LD from an ordered list of `{ name, path }` crumbs. */
export function breadcrumbJsonLd(crumbs: { name: string; path: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: crumbs.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.name,
      item: absoluteUrl(c.path),
    })),
  };
}

export {
  SITE_URL,
  SITE_NAME,
  MAX_TITLE_LENGTH,
  MAX_DESCRIPTION_LENGTH,
  absoluteUrl,
  shareImage,
};
